import { PREFIX_MAPLIBRE_LAYER, PREFIX_MAPLIBRE_SOURCE } from "../../plugins/aoi/consts";
import { GEOJSON_FEATURE_COLOR, GEOJSON_FEATURE_STROKE_COLOR, GEOJSON_FEATURE_STROKE_WIDTH } from "../consts";
import type { MapLayer } from "../types";
import { getBoundingPoints } from "./common";

export const expandUrl = (url: string) => (url.includes("{s}") ? ["a", "b", "c"].map((s) => url.replace(/\{s\}/g, s)) : [url]);

export async function createMapLibreLayer(cfg: MapLayer)
{
  const lid = `${PREFIX_MAPLIBRE_LAYER}${cfg.id}`;
  const sid = `${PREFIX_MAPLIBRE_SOURCE}${cfg.id}`;

  switch (cfg.type)
  {
    case "osm":
    case "xyz":
      return {
        source: { type: "raster", tiles: expandUrl(cfg.url ?? ""), tileSize: cfg?.xyzParams?.tileSize || 256 },
        layers: [{ id: lid, type: "raster", source: sid, paint: { "raster-opacity": cfg.opacity } }],
      };
      break;

    case "wms":
      const wmsParams = cfg.wmsParams ?? {
        version: "1.1.1",
        layers: "",
        format: "image/png",
        styles: "",
        tileSize: 256,
        crs: `EPSG:3857`,
        bbox: `{bbox-epsg-3857}`,
      };
      const srs = (wmsParams.version ?? "1.1.1").startsWith("1.3") ? "CRS" : "SRS";
      let wmsUrl = `${cfg.url}`;
      if (!wmsUrl?.includes("?") && wmsUrl.indexOf("?") < 0)
      {
        wmsUrl =
          `${cfg.url}?SERVICE=WMS&VERSION=${wmsParams.version ?? "1.1.1"}&REQUEST=GetMap` +
          `&LAYERS=${wmsParams.layers ?? ""}&FORMAT=${wmsParams.format ?? "image/png"}` +
          `&STYLES=${wmsParams.styles ?? ""}&${srs}=${wmsParams?.crs || "EPSG:3857"}` +
          `&WIDTH=${wmsParams?.tileSize || 256}&HEIGHT=${wmsParams?.tileSize || 256}` +
          `&BBOX=${wmsParams?.["bbox"] || "{bbox-epsg-3857}"}`;
        if (cfg?.wmsParams?.["transparent"]) wmsUrl += `&TRANSPARENT=${cfg?.wmsParams?.["transparent"]}`;
      }
      return {
        source: { type: "raster", tiles: [wmsUrl], tileSize: cfg?.wmsParams?.tileSize || 256 },
        layers: [{ id: lid, type: "raster", source: sid, paint: { "raster-opacity": cfg.opacity } }],
      };
      break;

    case "wmts":
      const wmtsParams = cfg.wmtsParams ?? {
        version: "1.0.0",
        layer: "",
        style: "",
        format: "image/png",
        tileMatrixSet: "EPSG:3857",
        tileSize: 256,
      };
      let wmtsUrl = cfg.url ?? "";
      if (wmtsUrl.includes("{TileMatrix}")) wmtsUrl = wmtsUrl.replace("{TileMatrix}", "{z}").replace("{TileRow}", "{y}").replace("{TileCol}", "{x}");
      else
      {
        if (!wmtsUrl?.includes("?") && wmtsUrl.indexOf("?") < 0)
        {
          wmtsUrl =
            `${wmtsUrl}?SERVICE=WMTS&REQUEST=GetTile&VERSION=${wmtsParams.version ?? "1.0.0"}` +
            `&LAYER=${wmtsParams.layer ?? ""}&STYLE=${wmtsParams.style ?? ""}&FORMAT=${wmtsParams.format ?? "image/png"}` +
            `&TILEMATRIXSET=${wmtsParams.tileMatrixSet ?? "EPSG:3857"}` +
            `&TILEMATRIX={z}&TILEROW={y}&TILECOL={x}`;
        }
      }

      return {
        source: { type: "raster", tiles: [wmtsUrl], tileSize: cfg?.wmtsParams?.tileSize || 256 },
        layers: [{ id: lid, type: "raster", source: sid, paint: { "raster-opacity": cfg.opacity } }],
      };
      break;
    case "cog":
      return {
        source: { type: "raster", url: cfg.url?.startsWith("cog://") ? cfg.url : `cog://${cfg.url}`, tileSize: cfg?.wmtsParams?.tileSize || 256 },
        layers: [{ id: lid, type: "raster", source: sid, paint: { "raster-opacity": cfg.opacity } }],
      };
      break;
    case "geojson":
    case "wfs":
      let data: object = { type: "FeatureCollection", features: [] };
      if (cfg.geojsonData)
      {
        data = cfg.geojsonData;
      } else
      {
        const fetchUrl =
          cfg.geojsonUrl ??
          (cfg.type === "wfs" && cfg.url
            ? `${cfg.url}?service=WFS&version=2.0.0&request=GetFeature` + `&typeName=${cfg.wmsParams?.layers ?? ""}&outputFormat=application/json`
            : null);
        if (fetchUrl)
        {
          try
          {
            const r = await fetch(fetchUrl);
            data = await r.json();
          } catch (e)
          {
            console.warn("[ML] fetch failed:", e);
          }
        }
      }

      return {
        source: { type: "geojson", data },
        layers: [
          {
            id: `${lid}-fill`,
            type: "fill",
            source: sid,
            paint: { "fill-color": ["coalesce", ["get", "color"], GEOJSON_FEATURE_COLOR], "fill-opacity": 0.18 },
            filter: ["==", ["geometry-type"], "Polygon"],
          },
          {
            id: `${lid}-line`,
            type: "line",
            source: sid,
            paint: { "line-color": ["coalesce", ["get", "color"], GEOJSON_FEATURE_STROKE_COLOR], "line-width": GEOJSON_FEATURE_STROKE_WIDTH },
          },
          {
            id: `${lid}-point`,
            type: "circle",
            source: sid,
            paint: {
              "circle-radius": 5,
              "circle-color": ["coalesce", ["get", "color"], GEOJSON_FEATURE_COLOR],
              "circle-stroke-width": GEOJSON_FEATURE_STROKE_WIDTH,
              "circle-stroke-color": GEOJSON_FEATURE_STROKE_COLOR,
            },
            filter: ["==", ["geometry-type"], "Point"],
          },
        ],
      };

      break;

    case "image":
      const supplied: [number, number][] = cfg?.imageParams?.coordinates || [];
      let imgCoordinates: [number, number][];

      if (cfg?.imageParams?.cornersOrdered && supplied.length === 4)
      {
        // Already the raster's own corners, in its own order. Re-deriving them
        // from the geography would undo exactly the rotation they carry.
        imgCoordinates = supplied;
      }
      else
      {
        // Named for screen space, so `topLeft` is the westernmost-southernmost
        // corner: for a north-up image that is the south-west one, and the list
        // below comes out as north-west, north-east, south-east, south-west.
        const { topLeft, topRight, bottomRight, bottomLeft } = getBoundingPoints(supplied);
        imgCoordinates = [
          [bottomLeft[0], bottomLeft[1]],
          [bottomRight[0], bottomRight[1]],
          [topRight[0], topRight[1]],
          [topLeft[0], topLeft[1]],
        ];
      }


      return {
        source: {
          type: "image",
          url:
            cfg?.imageParams?.urlType === "base64" ? (cfg?.url?.startsWith("data:image") ? cfg?.url : "data:image/png;base64," + cfg?.url) : cfg?.url,
          coordinates: imgCoordinates,
        },
        layers: [{ id: lid, type: "raster", source: sid, paint: { "raster-opacity": cfg.opacity } }],
      };
      break;
  }
  return { layers: [], source: null };
}
